import { formatDate } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { InsuranceFlowStatus } from 'src/app/_enums/insurance-flow/insurance-flow-status.enum';
import { ProposalState } from 'src/app/_enums/proposal.enums';
import { FileSizePipe } from 'src/app/_pipes/file-size.pipe';
import { ProposalService } from 'src/app/_services/proposal.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { APIResponse, BasicResponse } from 'src/app/_types/api-responses.types';
import { ProposalDetails } from 'src/app/_types/proposals.type';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { PdfViewerModalComponent } from 'src/app/shared/components/pdf-viewer-modal/pdf-viewer-modal.component';
import { ProposalDisplayerComponent } from 'src/app/shared/components/view-proposals/proposal-displayer/proposal-displayer.component';
import { syncProposalSegmentHeights } from 'src/app/shared/components/view-proposals/proposal-segment-heights';
import { IconsModule } from 'src/app/shared/icons/icons.module';

import { TaskDetailsService } from '../../../../task-details.service';
import { BaseStepperAction } from '../base-action';

@Component({
  selector: 'app-accept-offer-action',
  standalone: true,
  imports: [
    FormsModule,
    NgbTooltipModule,
    IconsModule,
    ActionModalComponent,
    ProposalDisplayerComponent,
    PdfViewerModalComponent,
    FileSizePipe,
  ],
  templateUrl: './accept-offer.component.html',
  styleUrl: './accept-offer.component.css',
})
export class AcceptOfferStepperActionComponent extends BaseStepperAction {
  @ViewChild('modal') modal!: ActionModalComponent;

  @ViewChild('pdfViewer') pdfViewer!: PdfViewerModalComponent;

  submitted = false;

  selectedProposalId: string | null = null;

  uploadedFile?: File;

  signatureDate = formatDate(new Date(), 'yyyy-MM-dd', 'fr');

  private acceptSubscription?: Subscription;

  private proposalHeightFrame?: number;

  constructor(
    taskDetailsService: TaskDetailsService,
    private readonly proposalService: ProposalService,
    private readonly userAuthService: UserAuthService,
    private readonly toastrService: ToastrService
  ) {
    super(taskDetailsService);
  }

  get proposals(): ProposalDetails[] {
    return (
      this.state?.proposals?.filter(
        (proposal) => proposal.proposalState === ProposalState.OFFER_CREATED
      ) ?? []
    );
  }

  get enabled(): boolean {
    return (
      this.state?.insuranceFlowData?.status ===
        InsuranceFlowStatus.PENDING_OFFERS && this.proposals.length > 0
    );
  }

  get canSubmit(): boolean {
    return (
      !!this.selectedProposalId && !!this.uploadedFile && !!this.signatureDate
    );
  }

  openModal(): void {
    this.resetForm();
    this.modal.openModal();
    this.proposalHeightFrame = requestAnimationFrame(() =>
      syncProposalSegmentHeights(
        document.querySelector('.accept-offer-modal'),
        '.accept-offer-select-btn'
      )
    );
  }

  resetForm(): void {
    this.selectedProposalId =
      this.proposals.length === 1 ? this.proposals[0].proposalId : null;
    this.uploadedFile = undefined;
    this.signatureDate = formatDate(new Date(), 'yyyy-MM-dd', 'fr');
    this.submitted = false;
  }

  selectProposal(proposalId: string): void {
    this.selectedProposalId = proposalId;
  }

  isSelected(proposalId: string): boolean {
    return this.selectedProposalId === proposalId;
  }

  onFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    [this.uploadedFile] = Array.from(input.files ?? []);
  }

  removeFile(): void {
    this.uploadedFile = undefined;
  }

  acceptOffer(): void {
    if (
      this.submitted ||
      !this.canSubmit ||
      !this.uploadedFile ||
      !this.selectedProposalId
    ) {
      return;
    }

    this.submitted = true;
    this.acceptSubscription = this.proposalService
      .acceptOffer({
        proposalId: this.selectedProposalId,
        signatureDate: new Date(this.signatureDate),
        offerDocument: this.uploadedFile,
      })
      .subscribe({
        next: (res) => {
          if (res.error) {
            this.toastrService.error(res.error.clientErrorMessage);
            this.submitted = false;
            return;
          }
          this.toastrService.success($localize`Offre acceptée avec succès`);
          this.modal.closeModal();
          this.refreshTaskDetails();
          this.submitted = false;
        },
        error: (err: APIResponse<BasicResponse>) => {
          this.toastrService.error(
            err.error?.clientErrorMessage ??
              $localize`Une erreur est survenue lors de l'acceptation de l'offre`
          );
          this.submitted = false;
        },
      });
  }

  viewFile(key: string): void {
    if (key.includes('https://')) {
      this.setFileUrl(key);
      return;
    }
    this.userAuthService.getPresignedUrl(key).subscribe((res) => {
      if (res.status === 'SUCCESS') {
        this.setFileUrl(res.data.preSignedUrl);
      }
    });
  }

  private setFileUrl(url: string): void {
    this.pdfViewer.pdfFileUrl = url;
    this.pdfViewer.openModal();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.proposalHeightFrame) {
      cancelAnimationFrame(this.proposalHeightFrame);
    }
    this.acceptSubscription?.unsubscribe();
  }
}
